import React, { useState } from 'react';
import { Check, Copy, KeyRound, X } from 'lucide-react';
import { formatDateTime } from '../utils';

/**
 * Exibe um segredo que o Core não guarda em texto claro.
 *
 * O aviso é parte da função: se a pessoa fechar sem copiar, o caminho é emitir
 * outro token ou outra rotação, porque o banco só tem o hash.
 */
function OneTimeSecretPanel({ title, description, value, expiresAt, onDismiss }) {
  const [copyState, setCopyState] = useState('idle');

  if (!value) {
    return null;
  }

  /** Cópia por seleção, que funciona onde a Clipboard API não está disponível. */
  const copyBySelection = () => {
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(field);
    }
  };

  /**
   * `navigator.clipboard.writeText` não é confiável para dar retorno: sem foco no
   * documento a promise fica pendente para sempre, sem resolver nem rejeitar, e
   * `await` puro deixava o clique sem resposta nenhuma. Por isso a chamada corre
   * contra um timeout e cai na cópia por seleção.
   *
   * O estado de falha é obrigatório: quem não vê retorno fecha o painel achando
   * que copiou, e o segredo é de uso único.
   */
  const handleCopy = async () => {
    const viaClipboardApi = navigator.clipboard?.writeText
      ? await Promise.race([
          navigator.clipboard.writeText(value).then(
            () => true,
            () => false,
          ),
          new Promise((resolve) => setTimeout(() => resolve(false), 400)),
        ])
      : false;

    // A confirmação não volta para o estado inicial: um aviso de dois segundos é
    // fácil de perder, e aqui a pessoa precisa de certeza de que capturou um
    // segredo que não será exibido de novo.
    setCopyState(viaClipboardApi || copyBySelection() ? 'copied' : 'failed');
  };

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <KeyRound className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">{title}</p>
            <p className="text-xs text-amber-800 mt-1">{description}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-amber-700 hover:text-amber-900 shrink-0"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-lg bg-white border border-amber-200 px-3 py-2 text-xs font-mono break-all text-slate-800">
          {value}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="btn-secondary text-sm px-3 py-2 inline-flex items-center gap-1.5 shrink-0"
        >
          {copyState === 'copied' ? (
            <Check className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <Copy className="h-4 w-4 shrink-0" aria-hidden />
          )}
          {copyState === 'copied' ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      {copyState === 'copied' && (
        <p className="text-xs text-emerald-800 font-medium flex items-center gap-1.5">
          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Copiado para a área de transferência.
        </p>
      )}

      {copyState === 'failed' && (
        <p className="text-xs text-amber-900 font-medium">
          Não foi possível copiar automaticamente neste navegador. Selecione o valor
          acima e copie à mão antes de fechar.
        </p>
      )}

      {expiresAt && (
        <p className="text-xs text-amber-800">
          Válido até {formatDateTime(expiresAt)}.
        </p>
      )}
    </div>
  );
}

export default OneTimeSecretPanel;
